import { capitalize } from '@mui/material'
import {
  PersonalSettingsColor,
  PersonalSettingsDimmed
} from 'assets/icons/group-details'
import {
  GroupMenuAllCalendarsOn,
  GroupMenuDefault,
  GroupMenuRemindersOn,
  GroupMenuYouOwn,
  GroupMenuYouOwnOff
} from 'assets/icons/group-menu'
import { useConnection } from 'components/providers/ConnectionProvider'
import { t } from 'i18next'
import { ChangeEvent, useState } from 'react'
import { localGroupById } from 'services/db/Queries/GroupQueries'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { isGroupBlockedToAddEvents } from 'utils/api/groups'
import { isGroupDefault } from 'utils/api/userSettings'
import { CurrentConvertedCalendars } from 'utils/google_events'
import { isConvertedGroup, isConvertedGroupId } from 'utils/groups'
import { removePrefix } from 'utils/object'
import { cacheConvertedCalendarsToLocalStorage } from 'utils/third_party_calendars'

import Switch from '../../../components/form/Switch'
import PopupSection from '../../../components/popup/PopupSection'
import PopupSectionRow from '../../../components/popup/PopupSectionRow'
import { updateGroupsSetting as updateGroupsSettingInUserSettings } from '../../../services/api/userSettings'
import Logger from '../../../services/Logger'
import {
  AccountDoc,
  GroupDoc,
  GroupStatus,
  UserSettingsDoc,
  UserSettingsGroupSettings
} from '../../../types/api/changes'
import { Model } from '../../../types/model'
import ColorsPopup from '../ColorsPopup'
import { ILoading } from './GroupPopup'

interface SectionForCalendarProps extends ILoading {
  group: Model<GroupDoc>
  userSettings: Model<UserSettingsDoc>
  account: Model<AccountDoc>
}

export default function SettingsForCalendarSection(
  props: SectionForCalendarProps
) {
  const connection = useConnection()
  const groupsSettings = props.userSettings?.GroupsSettings?.[props.group._id]
  const colorFromUserSettings = groupsSettings?.GroupColor

  async function updateGroupSettingInUserSetting(
    data: UserSettingsGroupSettings,
    groupId?: string
  ) {
    if (connection.isConnectionAvailable()) {
      props.setLoading(true)
      await performUpdateGroupSettings(
        props.userSettings,
        groupId ?? props.group._id,
        data
      )
      props.setLoading(false)
    }
  }

  const changeShowOnAllCalendar = (event: ChangeEvent<HTMLInputElement>) => {
    updateGroupSettingInUserSetting({
      ShowOnAllCalendars: event.target.checked ? '1' : '0'
    })
  }

  const changeReceiveReminders = (event: ChangeEvent<HTMLInputElement>) => {
    updateGroupSettingInUserSetting({
      ActivateReminders: event.target.checked ? '1' : '0'
    })
  }

  const changeDimmedEvents = (event: ChangeEvent<HTMLInputElement>) => {
    updateGroupSettingInUserSetting({
      ShoWEventsFromOtherGroups: event.target.checked ? '1' : '0'
    })
  }

  const changeShowOnlyYourEvents = (event: ChangeEvent<HTMLInputElement>) => {
    updateGroupSettingInUserSetting({
      ShoWOnlyEventsIOwn: event.target.checked ? '1' : '0'
    })
  }

  const changeDefaultCalendar = (event: ChangeEvent<HTMLInputElement>) => {
    updateGroupSettingInUserSetting({
      Default: event.target.checked ? '1' : '0'
    })
  }

  const onSetColor = (color: string) => {
    updateGroupSettingInUserSetting({
      GroupColor: color
    }).then(() => {
      setShowColorPopup(false)
    })
  }

  const [showColorPopup, setShowColorPopup] = useState(false)

  const showColors = () => {
    setShowColorPopup(true)
  }

  return (
    <PopupSection
      title={`${t('personalSettings')}`}
      simplified
      popupSectionGap={'8px'}
    >
      {isConvertedGroup(props.group) ? (
        <></>
      ) : (
        <PopupSectionRow
          title={`${capitalize(t('color'))}`}
          action={showColors}
          iconClass="icon-xs"
          icon={<PersonalSettingsColor />}
          explanation={t('colorExplanation')}
          value={
            <ColorsPopup
              open={showColorPopup}
              setOpen={setShowColorPopup}
              color={colorFromUserSettings ?? props.group.GroupColor}
              setColor={onSetColor}
            />
          }
        />
      )}
      <PopupSectionRow
        title={`${t('showEventsInAll', {
          allCalendars: t('allCalendarsButton')
        })}`}
        iconClass="icon-xs"
        icon={<GroupMenuAllCalendarsOn />}
        explanation={t('showEventsInAllExplanation')}
        value={
          <Switch
            checked={(groupsSettings?.ShowOnAllCalendars ?? '1') === '1'}
            onChange={changeShowOnAllCalendar}
          />
        }
      />
      <PopupSectionRow
        title={`${t('getRemindersForEvents')}`}
        iconClass="icon-xs"
        icon={<GroupMenuRemindersOn />}
        explanation={t('getRemindersForEventsExplanation')}
        value={
          <Switch
            checked={(groupsSettings?.ActivateReminders ?? '1') === '1'}
            onChange={changeReceiveReminders}
          />
        }
      />
      <PopupSectionRow
        title={`${t('dimmedEventsSettings')}`}
        iconClass="icon-xs"
        icon={<PersonalSettingsDimmed />}
        explanation={t('dimmedEventsSettingsExplanation')}
        value={
          <Switch
            checked={(groupsSettings?.ShoWEventsFromOtherGroups ?? '1') === '1'}
            onChange={changeDimmedEvents}
          />
        }
      />
      <PopupSectionRow
        title={`${t('showOnlyYourEvents')}`}
        iconClass="icon-xs"
        icon={
          (groupsSettings?.ShoWOnlyEventsIOwn ?? '0') === '1' ? (
            <GroupMenuYouOwnOff />
          ) : (
            <GroupMenuYouOwn />
          )
        }
        explanation={t('showOnlyYourEventsExplanation')}
        value={
          <Switch
            checked={(groupsSettings?.ShoWOnlyEventsIOwn ?? '0') === '1'}
            onChange={changeShowOnlyYourEvents}
          />
        }
      />
      {!isGroupBlockedToAddEvents(
        props.account,
        props.group,
        props.userSettings,
        LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
      ) && (
        <PopupSectionRow
          title={`${t('defaultCalendar')}`}
          icon={<GroupMenuDefault />}
          iconClass="icon-xs"
          explanation={t('defaultCalendarExplanation')}
          value={
            <Switch
              checked={isGroupDefault(props.userSettings, props.group._id)}
              onChange={changeDefaultCalendar}
            />
          }
        />
      )}
    </PopupSection>
  )
}

export async function performUpdateGroupSettings(
  userSettings: Model<UserSettingsDoc> | UserSettingsDoc | undefined,
  groupId: string,
  data: UserSettingsGroupSettings
) {
  if (userSettings) {
    try {
      await updateGroupsSettingInUserSettings(groupId, data)

      if (
        data.GroupStatus === GroupStatus.ARCHIVED &&
        isConvertedGroupId(groupId)
      ) {
        localGroupById(groupId)?.then((group) => {
          const primaryCalendarEmail = removePrefix(group.CalendarEmail ?? '')
          Logger.blue(
            'Group archived, removing from converted calendars',
            group,
            groupId
          )

          let removed = 0
          for (let i = CurrentConvertedCalendars.length - 1; i >= 0; i--) {
            if (
              CurrentConvertedCalendars[i].primaryCalendarEmail ===
              primaryCalendarEmail
            ) {
              CurrentConvertedCalendars.splice(i, 1)
              removed++
            }
          }

          Logger.blue('After cleanup', {
            primaryCalendarEmail,
            removed,
            remaining: CurrentConvertedCalendars.length
          })
        })
      }

      if (isConvertedGroupId(groupId)) {
        cacheConvertedCalendarsToLocalStorage()
      }

      window.dispatchEvent(
        new CustomEvent(DEFAULT_CALENDAR_CHANGED_EVENT, {
          detail: { groupId }
        })
      )
    } catch (e) {
      Logger.red('error update color', String(e))
    }
  } else {
    Logger.red('error update color', 'user settings not found')
  }
}

export const DEFAULT_CALENDAR_CHANGED_EVENT = 'default-calendar-changed'
